import { useState } from 'react';

interface RequestStateSuccess<T> {
    type: 'REQUEST_SUCCESS';
    data: T;
}
interface RequestStateError<ErrorResponse> {
    type: 'REQUEST_ERROR';
    error: IError<ErrorResponse>;
}

export type IError<ErrorResponse = unknown> = Error & {
    response?: {
        status: number;
        data?: ErrorResponse & {
            errors?: any;
            message: string;
        };
    };
};

export const isSucceeded = <T>(state: RequestState<T>): boolean =>
    state.type === 'REQUEST_SUCCESS';
export const isLoading = <T>(state: RequestState<T>): boolean =>
    state.type === 'REQUEST_START';
export const isFailed = <T>(state: RequestState<T>): boolean =>
    state.type === 'REQUEST_ERROR';

export const requestInit = () => ({ type: 'REQUEST_INIT' } as const);
export const requestStart = () => ({ type: 'REQUEST_START' } as const);
export const requestSuccess = <T>(data: T): RequestStateSuccess<T> =>
    ({ type: 'REQUEST_SUCCESS', data } as const);
export const requestError = <ErrorResponse = unknown>(
    error: IError<ErrorResponse>,
) => ({ type: 'REQUEST_ERROR', error } as const);

export const dataOrUndefined = <T>(request: RequestState<T>): T | undefined =>
    request.type === 'REQUEST_SUCCESS' ? request.data : undefined;

export type RequestState<T, TError = unknown> =
    | ReturnType<typeof requestInit | typeof requestStart>
    | RequestStateSuccess<T>
    | RequestStateError<TError>;

export const useRequestState = <T, TError = unknown>() => {
    const [requestState, setRequestState] = useState<RequestState<T, TError>>(
        requestInit(),
    );
    return [requestState, setRequestState] as const;
};
